import { useState } from 'react';

interface CalendarProps {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
  maxDaysFuture?: number;
}

export default function Calendar({ selectedDate, onDateSelect, maxDaysFuture = 29 }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const generateCalendarDays = () => {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + maxDaysFuture);
    
    const month = currentMonth.getMonth();
    const year = currentMonth.getFullYear();
    
    // Get first day of current month and how many days it has
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, disabled: true });
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      let startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const isDisabled = date < startOfToday || date > maxDate;
      const isSelected = selectedDate && new Date(selectedDate).toDateString() === date.toDateString();
      const isToday = date.toDateString() === today.toDateString();
      
      days.push({
        date: day,
        disabled: isDisabled,
        selected: isSelected,
        isToday: isToday,
        fullDate: date
      });
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const monthYearDisplay = currentMonth.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  const days = generateCalendarDays();

  return (
    <div className="ah-calendar">
      <div className="ah-calendar-header">
        <button 
          className="ah-calendar-nav" 
          onClick={() => navigateMonth('prev')}
          aria-label="Previous month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
        <div className="ah-calendar-month">
          {monthYearDisplay}
        </div>
        <button 
          className="ah-calendar-nav" 
          onClick={() => navigateMonth('next')}
          aria-label="Next month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9,18 15,12 9,6"></polyline>
          </svg>
        </button>
      </div>
      <div className="ah-calendar-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="ah-calendar-weekday">{day}</div>
        ))}
      </div>
      <div className="ah-calendar-days">
        {days.map((day, index) => (
          <button
            key={index}
            className={`ah-calendar-day ${day.selected ? 'selected' : ''} ${day.disabled ? 'disabled' : ''} ${day.isToday ? 'today' : ''}`}
            disabled={day.disabled}
            onClick={() => {
              if (!day.disabled && day.fullDate) {
                onDateSelect(day.fullDate);
              }
            }}
          >
            {day.date}
          </button>
        ))}
      </div>
      <div className="ah-calendar-footer">
        <button 
          className="ah-calendar-today-btn"
          onClick={() => {
            const today = new Date();
            setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
            onDateSelect(today);
          }}
        >
          Today
        </button>
      </div>
    </div>
  );
}
